import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { socialApi, type SocialConversationDetail, type SocialInboxItem } from "@/api/social";
import { toast } from "sonner";

function badgeClass(classification?: string | null) {
	if (classification === "LEAD") return "bg-emerald-100 text-emerald-800";
	if (classification === "MAYBE_LEAD") return "bg-amber-100 text-amber-900";
	if (classification === "NOT_LEAD") return "bg-slate-100 text-slate-600";
	return "bg-slate-100 text-slate-600";
}

export default function SocialInboxPage() {
	const qc = useQueryClient();
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [status, setStatus] = useState("all");
	const [conversationType, setConversationType] = useState("all");
	const [classification, setClassification] = useState("all");
	const [assigned, setAssigned] = useState("all");
	const [q, setQ] = useState("");

	const listQuery = useQuery({
		queryKey: ["social-inbox", status, conversationType, classification, assigned, q],
		queryFn: () =>
			socialApi.listInbox({
				status,
				conversationType,
				classification,
				assigned,
				q: q || undefined,
				take: 80,
			}),
		refetchInterval: 30_000,
	});

	const detailQuery = useQuery<SocialConversationDetail>({
		queryKey: ["social-inbox-detail", selectedId],
		queryFn: () => socialApi.getInbox(selectedId!),
		enabled: !!selectedId,
	});

	const actionMut = useMutation({
		mutationFn: (payload: {
			id: string;
			action: "convert" | "ignore" | "close" | "assign";
			assignedToId?: string;
		}) => socialApi.inboxAction(payload.id, payload),
		onSuccess: (_d, vars) => {
			toast.success(`Action: ${vars.action}`);
			qc.invalidateQueries({ queryKey: ["social-inbox"] });
			qc.invalidateQueries({ queryKey: ["social-inbox-detail", vars.id] });
		},
		onError: (e: any) => toast.error(e?.response?.data?.message || e.message || "Failed"),
	});

	const forceAskMut = useMutation({
		mutationFn: (id: string) => socialApi.forcePhoneAsk(id),
		onSuccess: (data: any) => {
			if (data?.ok) {
				toast.success("Phone-ask DM sent — check Instagram");
			} else {
				const hints = [
					data?.error,
					data?.probe?.error ? `Token: ${data.probe.error}` : null,
					data?.channel && !data.channel.hasAccessToken ? "No access token on channel" : null,
					data?.channel && !data.channel.pageId ? "Page ID missing" : null,
				]
					.filter(Boolean)
					.join(" · ");
				toast.error(hints || "Send failed — see Meta token / permissions");
			}
			qc.invalidateQueries({ queryKey: ["social-inbox"] });
			qc.invalidateQueries({ queryKey: ["social-inbox-detail", selectedId] });
		},
		onError: (e: any) => {
			const msg =
				e?.response?.data?.message ||
				e?.response?.data?.data?.error ||
				e.message ||
				"Send failed";
			toast.error(String(msg));
		},
	});

	const items: SocialInboxItem[] = listQuery.data?.items ?? [];
	const selected = useMemo(
		() => items.find((i) => i.id === selectedId) || null,
		[items, selectedId]
	);
	const detail = detailQuery.data;

	return (
		<div className="flex h-[calc(100vh-5rem)] min-h-[480px] flex-col gap-3 p-4 md:p-6">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Social Inbox</h1>
					<p className="text-sm text-muted-foreground">
						Instagram organic DMs &amp; comments — convert genuine enquiries to CRM leads.
					</p>
				</div>
				<div className="flex gap-2 text-sm">
					<Link className="underline-offset-4 hover:underline" to="/hospital-admin/social/settings">
						Settings
					</Link>
					<Link className="underline-offset-4 hover:underline" to="/hospital-admin/social/analytics">
						Analytics
					</Link>
				</div>
			</div>

			<div className="flex flex-wrap gap-2">
				<Input
					placeholder="Search username / text"
					value={q}
					onChange={(e) => setQ(e.target.value)}
					className="max-w-xs"
				/>
				<Select value={conversationType} onValueChange={setConversationType}>
					<SelectTrigger className="w-[140px]">
						<SelectValue placeholder="Type" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All types</SelectItem>
						<SelectItem value="DM">DM</SelectItem>
						<SelectItem value="COMMENT">Comment</SelectItem>
						<SelectItem value="STORY_REPLY">Story</SelectItem>
						<SelectItem value="MENTION">Mention</SelectItem>
					</SelectContent>
				</Select>
				<Select value={classification} onValueChange={setClassification}>
					<SelectTrigger className="w-[150px]">
						<SelectValue placeholder="Class" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All classes</SelectItem>
						<SelectItem value="LEAD">Lead</SelectItem>
						<SelectItem value="MAYBE_LEAD">Maybe</SelectItem>
						<SelectItem value="NOT_LEAD">Not lead</SelectItem>
					</SelectContent>
				</Select>
				<Select value={status} onValueChange={setStatus}>
					<SelectTrigger className="w-[140px]">
						<SelectValue placeholder="Status" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All status</SelectItem>
						<SelectItem value="OPEN">Open</SelectItem>
						<SelectItem value="REVIEW">Review</SelectItem>
						<SelectItem value="CONVERTED">Converted</SelectItem>
						<SelectItem value="IGNORED">Ignored</SelectItem>
						<SelectItem value="CLOSED">Closed</SelectItem>
					</SelectContent>
				</Select>
				<Select value={assigned} onValueChange={setAssigned}>
					<SelectTrigger className="w-[150px]">
						<SelectValue placeholder="Assigned" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All</SelectItem>
						<SelectItem value="assigned">Assigned</SelectItem>
						<SelectItem value="unassigned">Unassigned</SelectItem>
					</SelectContent>
				</Select>
			</div>

			<div className="grid min-h-0 flex-1 gap-3 md:grid-cols-[340px_1fr]">
				<div className="overflow-y-auto rounded-lg border bg-white">
					{listQuery.isLoading ? (
						<p className="p-4 text-sm text-muted-foreground">Loading…</p>
					) : items.length === 0 ? (
						<p className="p-4 text-sm text-muted-foreground">No conversations yet.</p>
					) : (
						<ul className="divide-y">
							{items.map((item) => (
								<li key={item.id}>
									<button
										type="button"
										className={`w-full px-3 py-3 text-left hover:bg-slate-50 ${
											selectedId === item.id ? "bg-slate-50" : ""
										}`}
										onClick={() => setSelectedId(item.id)}
									>
										<div className="flex items-center justify-between gap-2">
											<span className="truncate text-sm font-medium">
												{item.username ? `@${item.username}` : item.igUserId}
											</span>
											<span className="text-[10px] uppercase text-muted-foreground">
												{item.conversationType}
											</span>
										</div>
										<p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
											{item.lastMessagePreview || "—"}
										</p>
										<div className="mt-2 flex flex-wrap gap-1">
											<span
												className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${badgeClass(
													item.socialLead?.classification
												)}`}
											>
												{item.socialLead?.classification?.replace("_", " ") || "—"}
												{item.socialLead?.confidence != null
													? ` · ${item.socialLead.confidence}%`
													: ""}
											</span>
											<span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
												{item.status}
											</span>
										</div>
									</button>
								</li>
							))}
						</ul>
					)}
				</div>

				<div className="flex min-h-0 flex-col overflow-hidden rounded-lg border bg-white">
					{!selectedId ? (
						<div className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground">
							Select a conversation
						</div>
					) : detailQuery.isLoading ? (
						<p className="p-4 text-sm text-muted-foreground">Loading thread…</p>
					) : (
						<>
							<div className="border-b px-4 py-3">
								<div className="flex flex-wrap items-start justify-between gap-2">
									<div>
										<h2 className="text-lg font-semibold">
											{detail?.username
												? `@${detail.username}`
												: selected?.username
													? `@${selected.username}`
													: detail?.igUserId}
										</h2>
										<p className="text-xs text-muted-foreground">
											{detail?.conversationType} · {detail?.status}
											{detail?.socialLead?.procedure
												? ` · ${detail.socialLead.procedure}`
												: ""}
										</p>
										{detail?.socialLead?.reason ? (
											<p className="mt-1 text-xs text-muted-foreground">
												{detail.socialLead.reason}
											</p>
										) : null}
									</div>
									<div className="flex flex-wrap gap-2">
										<Button
											size="sm"
											disabled={actionMut.isPending}
											onClick={() =>
												actionMut.mutate({ id: selectedId, action: "convert" })
											}
										>
											Convert to lead
										</Button>
										<Button
											size="sm"
											variant="outline"
											disabled={actionMut.isPending}
											onClick={() =>
												actionMut.mutate({ id: selectedId, action: "assign" })
											}
										>
											Assign
										</Button>
										<Button
											size="sm"
											variant="outline"
											disabled={actionMut.isPending}
											onClick={() =>
												actionMut.mutate({ id: selectedId, action: "ignore" })
											}
										>
											Ignore
										</Button>
										<Button
											size="sm"
											variant="ghost"
											disabled={actionMut.isPending}
											onClick={() =>
												actionMut.mutate({ id: selectedId, action: "close" })
											}
										>
											Close
										</Button>
										<Button
											size="sm"
											variant="secondary"
											disabled={forceAskMut.isPending}
											onClick={() => forceAskMut.mutate(selectedId)}
										>
											{forceAskMut.isPending ? "Sending…" : "Send phone-ask now"}
										</Button>
									</div>
								</div>
								{detail?.socialLead?.leadId ? (
									<p className="mt-2 text-xs">
										CRM lead:{" "}
										<Link
											className="text-primary underline-offset-4 hover:underline"
											to={`/hospital-admin/leads?leadId=${detail.socialLead.leadId}`}
										>
											{detail.socialLead.lead?.patientName || detail.socialLead.leadId}
										</Link>
									</p>
								) : null}
							</div>
							<div className="flex-1 space-y-3 overflow-y-auto p-4">
								{(detail?.messages ?? []).map((m) => (
									<div
										key={m.id}
										className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
											m.direction === "INBOUND"
												? "bg-slate-100"
												: "ml-auto bg-sky-50"
										}`}
									>
										<p className="whitespace-pre-wrap">{m.text || "(no text)"}</p>
										<p className="mt-1 text-[10px] text-muted-foreground">
											{m.sentAt ? new Date(m.sentAt).toLocaleString() : ""}
										</p>
									</div>
								))}
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}
